import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, FileCheck, AlertCircle, Loader2, CheckCircle2, User, Calendar, FileText, Mail, Copy, Trash2, RefreshCw, FileSpreadsheet, Download, Eye } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { toast } from 'sonner';

// pdf.js worker — render PDFs to <canvas> instead of <iframe> so previews work
// regardless of the browser's "Download PDFs instead of opening" setting.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL;

// Return true if the Levenshtein edit distance between a and b is <= maxDist.
// Used for tolerant last-name matching (e.g. "Korolo" vs "Korola").
// Keep this O(n*m) — names are short.
function _levenshteinAtMost(a, b, maxDist) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > maxDist) return false;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n <= maxDist;
  if (n === 0) return m <= maxDist;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + cost
      );
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > maxDist) return false;
    [prev, curr] = [curr, prev];
  }
  return prev[n] <= maxDist;
}

export default function CredentialUpload() {
  const [activeTab, setActiveTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [tempFileId, setTempFileId] = useState(null);
  const [fileExt, setFileExt] = useState(null);
  
  // Form state for confirmation
  const [selectedTherapist, setSelectedTherapist] = useState('');
  const [credentialType, setCredentialType] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [documentName, setDocumentName] = useState('');
  
  // Data
  const [therapists, setTherapists] = useState([]);
  const [credentialTypes, setCredentialTypes] = useState([]);
  
  // Email reviews
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  
  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [isDraggingBulk, setIsDraggingBulk] = useState(false);
  // Background job state — persists across page navigation via localStorage
  const [bulkJobId, setBulkJobId] = useState(() => localStorage.getItem('bulk_analysis_job_id') || null);
  const [bulkJob, setBulkJob] = useState(null); // full server-side job doc
  // Track saved temp_file_ids so polling doesn't re-hydrate them
  const savedTempFileIdsRef = useRef(new Set());
  // Track whether we've done the final hydration (after job reached terminal state)
  // so subsequent polls don't blow away the user's manual edits.
  const hydrationFinalizedRef = useRef(false);
  
  // Document preview modal
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'image' or 'pdf'
  const [previewNumPages, setPreviewNumPages] = useState(0);
  const [previewError, setPreviewError] = useState(false);

  const openPreview = (tempFileId, fileExtension) => {
    const ext = (fileExtension || '').toLowerCase();
    const url = `${API}/api/credentials/temp-preview/${tempFileId}?token=${encodeURIComponent(localStorage.getItem('token') || '')}`;
    
    if (['.pdf'].includes(ext)) {
      setPreviewType('pdf');
    } else if (['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(ext)) {
      setPreviewType('image');
    } else {
      // For unknown types, open in new tab
      window.open(url, '_blank');
      return;
    }
    
    setPreviewUrl(url);
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setPreviewType(null);
    setPreviewNumPages(0);
    setPreviewError(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // All active therapists/employees (division is not reliably set on records)
        const [personnelRes, typesRes] = await Promise.all([
          axios.get(`${API}/api/personnel?status=Active`),
          axios.get(`${API}/api/credentials/types`)
        ]);
        // Map personnel to expected format with id field
        const therapists = (personnelRes.data || []).map(p => ({
          id: p.id,
          first_name: p.first_name,
          last_name: p.last_name,
          discipline: p.discipline
        })).sort((a, b) => 
          `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)
        );
        setTherapists(therapists);
        setCredentialTypes(typesRes.data.types);
      } catch (error) {
        toast.error('Failed to load data');
      }
    };
    fetchData();
  }, []);

  const fetchPendingReviews = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const response = await axios.get(`${API}/api/credentials/pending-reviews`);
      setPendingReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch pending reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'email') {
      fetchPendingReviews();
    }
  }, [activeTab, fetchPendingReviews]);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setAnalysisResult(null);
      setTempFileId(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setAnalysisResult(null);
      setTempFileId(null);
    }
  }, []);

  const handleAnalyze = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/api/credentials/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { temp_file_id, file_ext, analysis } = response.data;
      setTempFileId(temp_file_id);
      setFileExt(file_ext);
      setAnalysisResult(analysis);
      
      if (analysis.therapist_id) {
        setSelectedTherapist(analysis.therapist_id);
      }
      if (analysis.credential_type) {
        setCredentialType(analysis.credential_type);
        setDocumentName(analysis.credential_type);
      }
      if (analysis.expiration_date) {
        const parts = analysis.expiration_date.split('/');
        if (parts.length === 3) {
          setExpirationDate(`${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`);
        }
      }
      
      toast.success('Document analyzed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!tempFileId || !selectedTherapist || !credentialType) {
      toast.error('Please select a therapist and credential type');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/api/credentials/confirm`, {
        temp_file_id: tempFileId,
        therapist_id: selectedTherapist,
        credential_type: credentialType,
        expiration_date: expirationDate || null,
        document_name: documentName || credentialType
      });
      
      toast.success(`Credential saved for ${response.data.therapist_name}!`);
      
      setFile(null);
      setAnalysisResult(null);
      setTempFileId(null);
      setSelectedTherapist('');
      setCredentialType('');
      setExpirationDate('');
      setDocumentName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save credential');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectReview = (review) => {
    setSelectedReview(review);
    setTempFileId(review.temp_file_id);
    setFileExt(review.file_ext);
    
    if (review.analysis) {
      setAnalysisResult({
        therapist_name: review.analysis.therapist_name,
        therapist_id: review.analysis.therapist_id,
        credential_type: review.analysis.credential_type,
        expiration_date: review.analysis.expiration_date,
        confidence: review.status === 'auto_matched' ? 'high' : 'medium'
      });
      
      if (review.analysis.therapist_id) {
        setSelectedTherapist(review.analysis.therapist_id);
      }
      if (review.analysis.credential_type) {
        setCredentialType(review.analysis.credential_type);
        setDocumentName(review.analysis.credential_type);
      }
      if (review.analysis.expiration_date) {
        const parts = review.analysis.expiration_date.split('/');
        if (parts.length === 3) {
          setExpirationDate(`${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`);
        }
      }
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/api/credentials/pending-reviews/${reviewId}`);
      toast.success('Review deleted');
      fetchPendingReviews();
      if (selectedReview?.id === reviewId) {
        setSelectedReview(null);
        setAnalysisResult(null);
        setTempFileId(null);
      }
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleSaveEmailReview = async () => {
    if (!selectedReview || !selectedTherapist || !credentialType) {
      toast.error('Please select a therapist and credential type');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/api/credentials/confirm`, {
        temp_file_id: selectedReview.temp_file_id,
        therapist_id: selectedTherapist,
        credential_type: credentialType,
        expiration_date: expirationDate || null,
        document_name: documentName || credentialType
      });
      
      toast.success(`Credential saved for ${response.data.therapist_name}!`);
      
      // Delete the review after saving
      await axios.delete(`${API}/api/credentials/pending-reviews/${selectedReview.id}`);
      
      setSelectedReview(null);
      setAnalysisResult(null);
      setTempFileId(null);
      setSelectedTherapist('');
      setCredentialType('');
      setExpirationDate('');
      setDocumentName('');
      fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save credential');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleBulkFilesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setBulkFiles(files);
    setBulkResults([]);
  };

  const handleBulkDrop = useCallback((e) => {
    e.preventDefault();
    setIsDraggingBulk(false);
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    // Filter to only supported file types
    const validFiles = droppedFiles.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ['png', 'jpg', 'jpeg', 'pdf', 'gif', 'webp'].includes(ext);
    });
    if (validFiles.length > 0) {
      setBulkFiles(validFiles);
      setBulkResults([]);
      if (validFiles.length < droppedFiles.length) {
        toast.info(`${droppedFiles.length - validFiles.length} unsupported file(s) were skipped`);
      }
    } else if (droppedFiles.length > 0) {
      toast.error('No supported files found. Please use PNG, JPG, PDF, GIF, or WEBP files.');
    }
  }, []);

  const handleBulkDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDraggingBulk(true);
  }, []);

  const handleBulkDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDraggingBulk(false);
  }, []);

  // Fuzzy match a detected therapist name to the loaded therapist list.
  // Handles middle names, comma-separated formats, first-initial styles, and
  // duplicate last names by also requiring first-name token agreement.
  const findTherapistByName = (detectedName) => {
    if (!detectedName || therapists.length === 0) return '';

    // Normalize: strip punctuation, lowercase, collapse whitespace.
    const norm = (s) => (s || '')
      .toString()
      .toLowerCase()
      .replace(/[.,]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const detected = norm(detectedName);
    if (!detected) return '';

    // Tokenize and figure out which token is first vs last.
    // Convention: if the raw detected string contains a comma, treat the
    // text before the comma as the last name. Otherwise the FIRST token is
    // the first name and the LAST token is the last name (middle names are
    // ignored for matching).
    let dFirst = '';
    let dLast = '';
    const tokens = detected.split(' ').filter(Boolean);
    if (detectedName.includes(',')) {
      const [lastPart, firstPart = ''] = detectedName.split(',').map(s => norm(s));
      dLast = (lastPart.split(' ').filter(Boolean).slice(-1)[0]) || '';
      dFirst = (firstPart.split(' ').filter(Boolean)[0]) || '';
    } else if (tokens.length >= 2) {
      dFirst = tokens[0];
      dLast = tokens[tokens.length - 1];
    } else if (tokens.length === 1) {
      // Only one token — could be either; we'll try as a last name first.
      dLast = tokens[0];
    }

    // 1) Exact full-name match in any common ordering (cheap, high-precision).
    for (const t of therapists) {
      const fn = norm(t.first_name);
      const ln = norm(t.last_name);
      if (!fn && !ln) continue;
      const fl = `${fn} ${ln}`.trim();
      const lf = `${ln} ${fn}`.trim();
      if (detected === fl || detected === lf) return t.id;
    }

    // 2) First-initial + last-name match (e.g. "T. Korolo" or "T Korolo" → "Taiwo Korolo")
    for (const t of therapists) {
      const fn = norm(t.first_name);
      const ln = norm(t.last_name);
      if (!fn || !ln) continue;
      const fi = fn[0];
      if (dLast && dFirst && dFirst.length === 1 && dFirst === fi && dLast === ln) return t.id;
    }

    // 3) First + last token match, ignoring middle names.
    //    e.g. detected "Taiwo Kola Korolo" → first=Taiwo, last=Korolo → matches "Taiwo Korolo"
    if (dFirst && dLast) {
      const matches = therapists.filter(t => norm(t.first_name) === dFirst && norm(t.last_name) === dLast);
      if (matches.length === 1) return matches[0].id;
      // Partial first name (e.g. nickname): "Tai" vs "Taiwo"
      const partial = therapists.filter(t => {
        const fn = norm(t.first_name);
        const ln = norm(t.last_name);
        return fn && ln && ln === dLast && (fn.startsWith(dFirst) || dFirst.startsWith(fn));
      });
      if (partial.length === 1) return partial[0].id;
    }

    // 4) Unique last-name match (only if exactly ONE therapist has that last name).
    if (dLast) {
      const lastMatches = therapists.filter(t => norm(t.last_name) === dLast);
      if (lastMatches.length === 1) return lastMatches[0].id;

      // Tolerate small typos in last name (Levenshtein distance ≤ 1) so
      // "Korolo" vs "Korola" still matches when unambiguous.
      const close = therapists.filter(t => {
        const ln = norm(t.last_name);
        return ln && _levenshteinAtMost(ln, dLast, 1);
      });
      if (close.length === 1) return close[0].id;
    }

    // 5) Fallback: detected string CONTAINS a unique last name as a substring.
    //    Useful when the detected name has unusual punctuation we didn't parse.
    const containsMatches = therapists.filter(t => {
      const ln = norm(t.last_name);
      return ln && detected.includes(ln);
    });
    if (containsMatches.length === 1) return containsMatches[0].id;

    return '';
  };

  // Helper to hydrate a completed server job into the existing bulkResults shape
  // so the rest of the review/confirm flow keeps working unchanged.
  //
  // CRITICAL: This must NOT clobber user edits (selectedTherapist, credentialType,
  // expirationDate, documentName) once they've been made — otherwise the 3s poll
  // wipes the user's manual therapist selection. We merge by id from previous state.
  const hydrateResultsFromJob = useCallback((job) => {
    if (!job || !Array.isArray(job.files)) return;
    const savedIds = savedTempFileIdsRef.current;
    setBulkResults(prevRows => {
      const prevById = new Map(prevRows.map(r => [r.id, r]));
      const rows = [];
      job.files.forEach((f, i) => {
        // Skip files the user has already saved — don't re-hydrate them.
        if (f.temp_file_id && savedIds.has(f.temp_file_id)) return;

        if (f.status === 'error') {
          rows.push({
            id: i,
            fileName: f.original_name,
            error: f.error || 'Analysis failed',
            status: 'error',
          });
          return;
        }
        if (f.status !== 'done' || !f.analysis) return;
        const analysis = f.analysis;
        let formattedExpDate = '';
        if (analysis.expiration_date) {
          const parts = String(analysis.expiration_date).split('/');
          if (parts.length === 3) {
            formattedExpDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
        }
        const matched = analysis.therapist_id || findTherapistByName(analysis.therapist_name);
        const existing = prevById.get(i);
        // Preserve user edits if they exist; only seed from server on first hydration.
        rows.push({
          id: i,
          fileName: f.original_name,
          analysis,
          tempFileId: f.temp_file_id,
          fileExt: f.file_ext,
          selectedTherapist: existing?.selectedTherapist !== undefined
            ? existing.selectedTherapist
            : matched,
          credentialType: existing?.credentialType !== undefined
            ? existing.credentialType
            : (analysis.credential_type || ''),
          expirationDate: existing?.expirationDate !== undefined
            ? existing.expirationDate
            : formattedExpDate,
          documentName: existing?.documentName !== undefined
            ? existing.documentName
            : (analysis.credential_type || ''),
          therapistName: analysis.therapist_name || '',
          confidence: analysis.confidence || 'low',
          status: existing?.status || (matched ? 'ready' : 'needs_review'),
        });
      });
      return rows;
    });
  }, []);

  // Re-run the name matcher whenever the therapist list changes (e.g. arrived
  // after the first hydration) or whenever new analyzed rows appear.
  // This only fills in rows that DO NOT yet have a manually-selected therapist
  // and that have a detected name from the AI — so we never clobber user edits.
  useEffect(() => {
    if (!therapists || therapists.length === 0) return;
    if (!bulkResults || bulkResults.length === 0) return;
    setBulkResults(prev => {
      let changed = false;
      const next = prev.map(r => {
        if (r.error) return r;
        if (r.selectedTherapist) return r; // already matched / user-picked
        const detected = r.therapistName || r.analysis?.therapist_name;
        if (!detected) return r;
        const matched = findTherapistByName(detected);
        if (!matched) return r;
        changed = true;
        return { ...r, selectedTherapist: matched, status: 'ready' };
      });
      return changed ? next : prev;
    });
    // findTherapistByName uses `therapists` from closure, so re-creating this
    // effect every therapist-list change is exactly what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapists, bulkResults.length]);

  // Poll the server job — continues while the tab is open, and restores on
  // fresh mount if localStorage has a job_id from a prior session.
  //
  // Once the job reaches a terminal state (completed/cancelled/failed) we do
  // one final hydration and stop polling. This prevents the 3s tick from
  // continually overwriting the user's manual edits (e.g., the therapist they
  // just selected) while they review the results.
  useEffect(() => {
    if (!bulkJobId) return;
    // Reset the finalized flag whenever we switch to a new job.
    hydrationFinalizedRef.current = false;
    let cancelled = false;
    let intervalId = null;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/api/credentials/bulk-analyze/jobs/${bulkJobId}`);
        if (cancelled) return;
        setBulkJob(res.data);
        const isTerminal = ['completed', 'cancelled', 'failed'].includes(res.data.status);
        // Hydrate while job is still running (so progress is visible),
        // OR once when it first reaches terminal state.
        if (!hydrationFinalizedRef.current) {
          hydrateResultsFromJob(res.data);
          if (isTerminal) {
            hydrationFinalizedRef.current = true;
          }
        }
        if (isTerminal) {
          setBulkAnalyzing(false);
          // Stop polling — user edits are now safe.
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        } else {
          setBulkAnalyzing(true);
        }
      } catch (e) {
        if (e.response?.status === 404) {
          // Job was deleted elsewhere — clean up
          localStorage.removeItem('bulk_analysis_job_id');
          setBulkJobId(null);
          setBulkJob(null);
          setBulkAnalyzing(false);
          savedTempFileIdsRef.current = new Set();
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      }
    };
    tick();
    intervalId = setInterval(tick, 3000);
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
  }, [bulkJobId, hydrateResultsFromJob]);

  const handleBulkAnalyze = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    try {
      setBulkAnalyzing(true);
      setBulkResults([]);
      savedTempFileIdsRef.current = new Set();
      hydrationFinalizedRef.current = false;

      // 1) Create an empty job — small request, no payload size risk.
      const createRes = await axios.post(`${API}/api/credentials/bulk-analyze/create`);
      const job_id = createRes.data.job_id;
      localStorage.setItem('bulk_analysis_job_id', job_id);
      setBulkJobId(job_id);

      // 2) Upload files one at a time so each request stays well under the
      //    ingress proxy's body-size / timeout limits. We run with a small
      //    parallel batch for throughput but each individual request is tiny.
      const total = bulkFiles.length;
      let uploaded = 0;
      let skipped = 0;
      const uploadOne = async (f) => {
        const fd = new FormData();
        fd.append('file', f);
        try {
          const res = await axios.post(
            `${API}/api/credentials/bulk-analyze/jobs/${job_id}/files`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          if (res.data?.added === false) skipped++;
        } catch (e) {
          skipped++;
          console.error('Failed to upload', f.name, e);
        }
        uploaded++;
        // Lightweight toast update every 10 files (or last file)
        if (uploaded % 10 === 0 || uploaded === total) {
          toast.info(`Uploading ${uploaded}/${total}…`, { id: 'bulk-upload-progress' });
        }
      };

      // Parallel batch size of 4 — modest concurrency, keeps each request small.
      const CONCURRENCY = 4;
      const queue = [...bulkFiles];
      const workers = Array(Math.min(CONCURRENCY, queue.length)).fill(0).map(async () => {
        while (queue.length > 0) {
          const f = queue.shift();
          if (!f) break;
          await uploadOne(f);
        }
      });
      await Promise.all(workers);
      toast.dismiss('bulk-upload-progress');

      // 3) Start analysis on the server.
      const startRes = await axios.post(`${API}/api/credentials/bulk-analyze/jobs/${job_id}/start`);
      const { total: jobTotal, queued, pre_failed } = startRes.data;

      setBulkFiles([]); // staged files are now on the server
      toast.success(
        `Background job started · ${queued} of ${jobTotal} queued for AI analysis` +
        `${pre_failed ? ` (${pre_failed} unsupported skipped)` : ''}` +
        `${skipped ? ` · ${skipped} failed to upload` : ''}. ` +
        `You can leave this page and come back — it'll keep running.`
      );
    } catch (error) {
      setBulkAnalyzing(false);
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('Bulk analyze failed:', error);
      toast.error(`Failed to start background job: ${detail}`);
    }
  };

  const handleCancelBulkJob = async () => {
    if (!bulkJobId) return;
    if (!window.confirm('Cancel the background analysis? Files already analyzed will still be reviewable.')) return;
    try {
      await axios.post(`${API}/api/credentials/bulk-analyze/jobs/${bulkJobId}/cancel`);
      toast.success('Job cancelled');
    } catch (error) {
      toast.error('Failed to cancel job');
    }
  };

  const [retryingFailed, setRetryingFailed] = useState(false);
  const handleRetryFailedBulk = async () => {
    if (!bulkJobId) return;
    try {
      setRetryingFailed(true);
      // Clear local error rows so they're re-hydrated from server progress.
      setBulkResults(prev => prev.filter(r => r.status !== 'error'));
      hydrationFinalizedRef.current = false;
      const res = await axios.post(`${API}/api/credentials/bulk-analyze/jobs/${bulkJobId}/retry-failed`);
      toast.success(`Re-queued ${res.data.retried} failed file${res.data.retried === 1 ? '' : 's'} for analysis`);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Retry failed: ${detail}`);
    } finally {
      setRetryingFailed(false);
    }
  };

  const handleDismissBulkJob = async () => {
    if (!bulkJobId) return;
    if (bulkResults.some(r => r.status === 'ready' || r.status === 'needs_review')) {
      if (!window.confirm('Dismiss this job? Any unsaved analyzed documents will be discarded.')) return;
    }
    try {
      await axios.delete(`${API}/api/credentials/bulk-analyze/jobs/${bulkJobId}`);
    } catch {}
    localStorage.removeItem('bulk_analysis_job_id');
    setBulkJobId(null);
    setBulkJob(null);
    setBulkResults([]);
    setBulkAnalyzing(false);
    savedTempFileIdsRef.current = new Set();
    hydrationFinalizedRef.current = false;
  };

  const updateBulkResult = (id, field, value) => {
    setBulkResults(prev => prev.map(r => 
      r.id === id ? { ...r, [field]: value, status: 'ready' } : r
    ));
  };

  const removeBulkResult = (id) => {
    setBulkResults(prev => prev.filter(r => r.id !== id));
  };

  const handleBulkSave = async () => {
    const readyResults = bulkResults.filter(r => r.status === 'ready' && r.selectedTherapist);
    
    if (readyResults.length === 0) {
      toast.error('No credentials ready to save. Please select a therapist for each.');
      return;
    }
    
    setBulkSaving(true);
    let saved = 0;
    let failed = 0;
    let expiredFiles = 0; // temp file missing on server (e.g. after redeploy)
    const savedIds = [];
    const failureReasons = new Map(); // unique error messages → count
    
    for (const result of readyResults) {
      try {
        await axios.post(`${API}/api/credentials/confirm`, {
          temp_file_id: result.tempFileId,
          file_extension: result.fileExt,
          therapist_id: result.selectedTherapist,
          credential_type: result.credentialType,
          expiration_date: result.expirationDate || null,
          document_name: result.documentName || result.credentialType
        });
        saved++;
        if (result.tempFileId) savedIds.push(result.tempFileId);
      } catch (error) {
        failed++;
        const status = error.response?.status;
        const detail = error.response?.data?.detail || error.message || 'Unknown error';
        if (status === 404 && /temp|expired|not found/i.test(detail)) {
          expiredFiles++;
        }
        failureReasons.set(detail, (failureReasons.get(detail) || 0) + 1);
        console.error(`Failed to save ${result.fileName}:`, status, detail);
      }
    }
    
    setBulkSaving(false);
    
    // Track which temp_file_ids have been saved so polling won't re-hydrate them.
    if (savedIds.length > 0) {
      const next = new Set(savedTempFileIdsRef.current);
      savedIds.forEach(id => next.add(id));
      savedTempFileIdsRef.current = next;
    }
    
    if (saved > 0) {
      toast.success(`Saved ${saved} credential${saved > 1 ? 's' : ''}`);
      // Remove saved items from the visible list
      setBulkResults(prev => {
        const remaining = prev.filter(r => !savedIds.includes(r.tempFileId));
        // If nothing left to review and the job has finished analyzing, auto-dismiss
        // so the user is not stuck with an "upload in progress" banner.
        const anyReviewable = remaining.some(r => r.status === 'ready' || r.status === 'needs_review');
        if (!anyReviewable && bulkJob && ['completed', 'cancelled', 'failed'].includes(bulkJob.status) && bulkJobId) {
          // Fire-and-forget cleanup
          axios.delete(`${API}/api/credentials/bulk-analyze/jobs/${bulkJobId}`).catch(() => {});
          localStorage.removeItem('bulk_analysis_job_id');
          setBulkJobId(null);
          setBulkJob(null);
          savedTempFileIdsRef.current = new Set();
          hydrationFinalizedRef.current = false;
        }
        return remaining;
      });
      setBulkFiles([]);
    }
    if (failed > 0) {
      // Special case: every failure is "temp file missing" → the upload session
      // is stale (likely a redeploy/container restart wiped the staging files
      // BEFORE durable storage was enabled for those files). They're not
      // recoverable — auto-dismiss the job so the user isn't stuck, and tell
      // them clearly to re-upload.
      if (expiredFiles === failed) {
        // Auto-dismiss the dead job so the user can't get into a confusing
        // "save fails forever" loop. New uploads after today's deploy will
        // not have this problem (every upload now writes to durable storage).
        if (bulkJobId) {
          try {
            await axios.delete(`${API}/api/credentials/bulk-analyze/jobs/${bulkJobId}`);
          } catch (_) { /* ignore */ }
          localStorage.removeItem('bulk_analysis_job_id');
          setBulkJobId(null);
          setBulkJob(null);
          setBulkResults([]);
          savedTempFileIdsRef.current = new Set();
          hydrationFinalizedRef.current = false;
        }
        toast.error(
          `${failed} credential${failed > 1 ? 's' : ''} could not be saved — the staging files are no longer on the server (this batch was uploaded before durable storage was enabled, or the server was restarted before you saved). This job has been dismissed for you. Please re-upload the files; any new upload is now durably stored and will survive future redeploys.`,
          { duration: 15000 }
        );
      } else {
        // Pick the most common failure reason to surface
        let topReason = null;
        let topCount = 0;
        for (const [reason, count] of failureReasons.entries()) {
          if (count > topCount) { topReason = reason; topCount = count; }
        }
        toast.error(
          `${failed} credential${failed > 1 ? 's' : ''} failed to save${topReason ? ` — ${topReason}` : ''}`,
          { duration: 10000 }
        );
      }
    }
  };

  const downloadTemplate = () => {
    const csvContent = "therapist_name,credential_type,expiration_date\nJohn Doe,Physical Therapy License,2025-12-31\nJane Smith,CPR Certification,2025-06-15";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'credential_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      default: return 'text-red-600 bg-red-50';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'auto_matched':
        return <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Auto-matched</span>;
      case 'pending_review':
        return <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">Needs Review</span>;
      case 'analysis_failed':
        return <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">Analysis Failed</span>;
      default:
        return <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
          Credential Upload
        </h1>
        <p className="text-muted-foreground mt-2 leading-relaxed">
          Upload therapist credentials for automatic analysis and storage
        </p>
      </div>

      {/* Tab Navigation - Just Upload and Email */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'upload'
              ? 'border-sage text-sage'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-upload"
        >
          <Upload size={16} className="inline mr-2" />
          Upload Credentials
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'email'
              ? 'border-sage text-sage'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-email"
        >
          <Mail size={16} className="inline mr-2" />
          Email Upload
          {pendingReviews.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-sage text-white rounded-full">
              {pendingReviews.length}
            </span>
          )}
        </button>
      </div>

      {/* Unified Upload Tab */}
      {activeTab === 'upload' && (
        <>
        {/* Background job progress banner — visible across navigations until dismissed */}
        {bulkJob && (
          <Card className={`p-4 mb-4 border-2 ${
            bulkJob.status === 'completed' ? 'border-emerald-300 bg-emerald-50/60' :
            bulkJob.status === 'cancelled' || bulkJob.status === 'failed' ? 'border-amber-300 bg-amber-50/60' :
            'border-primary/30 bg-primary/5'
          }`} data-testid="bulk-job-banner">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-[260px]">
                {bulkJob.status === 'running' || bulkJob.status === 'queued'
                  ? <Loader2 className="animate-spin text-primary shrink-0" size={22} />
                  : bulkJob.status === 'completed'
                    ? <CheckCircle2 className="text-emerald-600 shrink-0" size={22} />
                    : <AlertCircle className="text-amber-600 shrink-0" size={22} />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    {bulkJob.status === 'running' && 'Analyzing in the background…'}
                    {bulkJob.status === 'queued' && 'Queued — starting shortly…'}
                    {bulkJob.status === 'completed' && 'Analysis complete — review below'}
                    {bulkJob.status === 'cancelled' && 'Analysis cancelled'}
                    {bulkJob.status === 'failed' && 'Analysis failed'}
                    <span className="text-xs font-normal text-muted-foreground">
                      {bulkJob.processed}/{bulkJob.total} files · {bulkJob.failed || 0} errors
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-muted mt-1.5 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        bulkJob.status === 'completed' ? 'bg-emerald-500' :
                        bulkJob.status === 'cancelled' || bulkJob.status === 'failed' ? 'bg-amber-500' :
                        'bg-primary'
                      }`}
                      style={{ width: `${bulkJob.total > 0 ? Math.round((bulkJob.processed / bulkJob.total) * 100) : 0}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {(bulkJob.status === 'running' || bulkJob.status === 'queued')
                      ? "You can leave this page — the job keeps running. Come back any time."
                      : bulkJob.status === 'completed'
                        ? "Click \"Dismiss\" once you've reviewed & saved the documents below."
                        : "Partial results are available below."}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {(bulkJob.status === 'running' || bulkJob.status === 'queued') && (
                  <Button variant="outline" size="sm" onClick={handleCancelBulkJob} data-testid="cancel-bulk-job-btn">
                    Cancel
                  </Button>
                )}
                {(bulkJob.status === 'completed' || bulkJob.status === 'cancelled' || bulkJob.status === 'failed') && (bulkJob.failed || 0) > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetryFailedBulk}
                    disabled={retryingFailed}
                    data-testid="retry-failed-bulk-btn"
                  >
                    {retryingFailed ? <Loader2 className="animate-spin mr-1.5" size={14} /> : null}
                    Retry failed ({bulkJob.failed})
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={handleDismissBulkJob} data-testid="dismiss-bulk-job-btn">
                  Dismiss
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: File Selection */}
          <Card className="p-6">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <Upload size={20} />
              Select Files
            </h2>
            
            <div 
              className={`border-2 border-dashed rounded-lg p-8 transition-colors ${
                isDraggingBulk 
                  ? 'border-sage bg-sage/10' 
                  : 'border-border hover:border-sage/50'
              }`}
              onDrop={handleBulkDrop}
              onDragOver={handleBulkDragOver}
              onDragLeave={handleBulkDragLeave}
            >
              <div className="text-center">
                <Upload className={`mx-auto mb-4 ${isDraggingBulk ? 'text-sage' : 'text-muted-foreground'}`} size={48} />
                <p className="text-sm font-medium mb-2">
                  Drag & drop credential files here
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  or click to select • PNG, JPG, PDF, GIF, WEBP
                </p>
                <Input
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf,.gif,.webp"
                  multiple
                  onChange={handleBulkFilesSelect}
                  className="max-w-md mx-auto"
                  data-testid="credential-file-input"
                />
                {bulkFiles.length > 0 && (
                  <p className="mt-3 text-sm font-medium text-sage">
                    {bulkFiles.length} file{bulkFiles.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <Button
                onClick={handleBulkAnalyze}
                disabled={bulkFiles.length === 0 || bulkAnalyzing}
                className="flex-1 gap-2"
                data-testid="analyze-credential-btn"
              >
                {bulkAnalyzing ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    {bulkJob ? 'Running in background…' : 'Starting…'}
                  </>
                ) : (
                  <>
                    <FileText size={16} />
                    Start Background Analysis{bulkFiles.length > 0 ? ` (${bulkFiles.length})` : ''}
                  </>
                )}
              </Button>
              {bulkFiles.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => { setBulkFiles([]); setBulkResults([]); }}
                >
                  Clear
                </Button>
              )}
            </div>
          </Card>

          {/* Right: Analysis Results */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                <CheckCircle2 size={20} />
                Review & Confirm
              </h2>
              {bulkResults.filter(r => !r.error && r.selectedTherapist && r.credentialType).length > 0 && (
                <Button
                  onClick={handleBulkSave}
                  disabled={bulkSaving}
                  className="gap-2"
                  data-testid="save-all-btn"
                >
                  {bulkSaving ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Save All ({bulkResults.filter(r => !r.error && r.selectedTherapist && r.credentialType).length})
                    </>
                  )}
                </Button>
              )}
            </div>

            {bulkResults.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="mx-auto mb-4 opacity-50" size={48} />
                <p>Select files and click &quot;Analyze&quot; to see results</p>
                <p className="text-sm mt-2">AI will detect therapist names, document types, and expiration dates</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {bulkResults.map((result) => (
                  <div 
                    key={result.id} 
                    className={`p-4 rounded-lg border ${
                      result.status === 'error' ? 'border-red-200 bg-red-50' :
                      result.status === 'ready' && result.selectedTherapist ? 'border-green-200 bg-green-50' :
                      'border-amber-200 bg-amber-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FileCheck size={16} className={`shrink-0 ${result.status === 'error' ? 'text-red-600' : 'text-sage'}`} />
                        <span className="text-sm font-medium truncate" title={result.fileName}>{result.fileName}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {result.tempFileId && !result.error && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openPreview(result.tempFileId, result.fileExt)}
                            className="text-sage hover:text-sage/80 h-7 px-2"
                            title="View document"
                          >
                            <Eye size={14} />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeBulkResult(result.id)}
                          className="text-muted-foreground hover:text-destructive h-7 px-2"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    
                    {result.error ? (
                      <p className="text-sm text-red-600">{result.error}</p>
                    ) : (
                      <div className="space-y-2">
                        {/* AI Detection Info */}
                        {result.therapistName && (
                          <div className="flex items-center justify-between text-xs bg-white/50 rounded px-2 py-1">
                            <span className="text-muted-foreground">
                              <User size={12} className="inline mr-1" />
                              Detected: <span className="font-medium text-foreground">{result.therapistName}</span>
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceColor(result.confidence)}`}>
                              {result.confidence}
                            </span>
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-muted-foreground">Therapist</label>
                          <Select 
                            value={result.selectedTherapist} 
                            onValueChange={(v) => updateBulkResult(result.id, 'selectedTherapist', v)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Select therapist..." />
                            </SelectTrigger>
                            <SelectContent>
                              {therapists.map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.last_name}, {t.first_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-muted-foreground">Type</label>
                            <Select 
                              value={result.credentialType} 
                              onValueChange={(v) => updateBulkResult(result.id, 'credentialType', v)}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {credentialTypes.map(t => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Expiration</label>
                            <Input 
                              type="date" 
                              value={result.expirationDate} 
                              onChange={(e) => updateBulkResult(result.id, 'expirationDate', e.target.value)}
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
        </>
      )}

      {activeTab === 'email' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Email Setup Instructions */}
          <Card className="p-6">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <Mail size={20} />
              Email Upload Setup
            </h2>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">How it works</h3>
                <p className="text-sm text-blue-800">
                  Configure CloudMailin to forward credential emails to your webhook. 
                  Documents will be automatically analyzed and added to the pending review queue.
                </p>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-medium">Setup Instructions:</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Sign up at <a href="https://cloudmailin.com" target="_blank" rel="noopener noreferrer" className="text-sage hover:underline">cloudmailin.com</a></li>
                  <li>Create a new email address</li>
                  <li>Set the webhook URL to:</li>
                </ol>
                
                <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-3">
                  <code className="text-xs flex-1 break-all">
                    {API}/api/credentials/email-webhook
                  </code>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(`${API}/api/credentials/email-webhook`)}
                  >
                    <Copy size={14} />
                  </Button>
                </div>
                
                <ol start={4} className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Set POST format to &quot;JSON (Normalized)&quot;</li>
                  <li>Enable &quot;Include Attachments&quot;</li>
                  <li>Forward credential emails to your CloudMailin address</li>
                </ol>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="font-medium text-amber-900 mb-1">Supported File Types</h3>
                <p className="text-sm text-amber-800">
                  PNG, JPG, JPEG, GIF, WEBP, PDF
                </p>
              </div>
            </div>
          </Card>

          {/* Pending Reviews */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                <FileText size={20} />
                Pending Reviews
              </h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchPendingReviews}
                disabled={loadingReviews}
              >
                <RefreshCw size={14} className={loadingReviews ? 'animate-spin' : ''} />
              </Button>
            </div>
            
            {loadingReviews ? (
              <div className="text-center py-8">
                <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                <p className="text-sm text-muted-foreground">Loading reviews...</p>
              </div>
            ) : pendingReviews.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Mail className="mx-auto mb-3" size={48} strokeWidth={1} />
                <p>No pending email reviews</p>
                <p className="text-sm mt-1">Credentials sent via email will appear here</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {pendingReviews.map((review) => (
                  <div
                    key={review.id}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      selectedReview?.id === review.id ? 'border-sage bg-sage/5' : 'hover:bg-muted/30'
                    }`}
                    onClick={() => handleSelectReview(review)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{review.original_filename}</p>
                        <p className="text-xs text-muted-foreground truncate">From: {review.from_email}</p>
                        {review.analysis?.therapist_name && (
                          <p className="text-xs text-muted-foreground">
                            Detected: {review.analysis.therapist_name}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        {getStatusBadge(review.status)}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteReview(review.id);
                          }}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {selectedReview && (
              <div className="mt-4 pt-4 border-t space-y-4">
                <h3 className="font-medium">Confirm Selected Review</h3>
                
                {selectedReview.analysis && (
                  <div className="bg-muted/30 rounded-lg p-3 space-y-1 text-sm">
                    {selectedReview.analysis.therapist_name && (
                      <p><span className="text-muted-foreground">Name:</span> {selectedReview.analysis.therapist_name}</p>
                    )}
                    {selectedReview.analysis.credential_type && (
                      <p><span className="text-muted-foreground">Type:</span> {selectedReview.analysis.credential_type}</p>
                    )}
                    {selectedReview.analysis.expiration_date && (
                      <p><span className="text-muted-foreground">Expires:</span> {selectedReview.analysis.expiration_date}</p>
                    )}
                  </div>
                )}
                
                <div className="space-y-3">
                  <Select value={selectedTherapist} onValueChange={setSelectedTherapist}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select therapist..." />
                    </SelectTrigger>
                    <SelectContent>
                      {therapists.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.last_name}, {t.first_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={credentialType} onValueChange={(v) => { setCredentialType(v); setDocumentName(v); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {credentialTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    placeholder="Expiration date"
                  />
                  
                  <Button
                    onClick={handleSaveEmailReview}
                    disabled={saving || !selectedTherapist || !credentialType}
                    className="w-full gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={16} />
                        Save Credential
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewUrl && (
        <div 
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" 
          onClick={closePreview}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between bg-muted/30">
              <h3 className="font-semibold flex items-center gap-2">
                <Eye size={18} />
                Document Preview
              </h3>
              <Button variant="ghost" onClick={closePreview} className="h-8 w-8 p-0">
                ✕
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4 flex flex-col items-center justify-start min-h-[400px] bg-muted/30">
              {previewType === 'image' ? (
                <img 
                  src={previewUrl} 
                  alt="Document preview" 
                  className="max-w-full max-h-[75vh] object-contain m-auto"
                />
              ) : previewType === 'pdf' ? (
                previewError ? (
                  <div className="flex flex-col items-center gap-3 text-center m-auto">
                    <AlertCircle size={40} className="text-red-500" />
                    <p className="text-sm font-medium">This PDF could not be rendered. Try Download instead.</p>
                  </div>
                ) : (
                  <Document
                    key={previewUrl}
                    file={previewUrl}
                    onLoadSuccess={({ numPages }) => setPreviewNumPages(numPages)}
                    onLoadError={() => setPreviewError(true)}
                    loading={
                      <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground py-16 m-auto">
                        <Loader2 size={28} className="animate-spin" />
                        <span className="text-sm">Loading…</span>
                      </div>
                    }
                  >
                    {Array.from({ length: previewNumPages }, (_, i) => (
                      <div key={i} className="mb-4 shadow-lg bg-white" data-testid={`upload-preview-pdf-page-${i + 1}`}>
                        <Page
                          pageNumber={i + 1}
                          width={760}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </div>
                    ))}
                  </Document>
                )
              ) : (
                <p className="text-muted-foreground m-auto">Preview not available for this file type</p>
              )}
            </div>
            <div className="p-4 border-t bg-muted/30 flex justify-end gap-2">
              <Button variant="outline" onClick={closePreview}>
                Close
              </Button>
              <Button onClick={() => window.open(previewUrl, '_blank')} className="gap-2">
                <Download size={16} />
                Download
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
